import React, {
  forwardRef,
  MouseEventHandler,
  ReactElement,
  RefObject,
  useEffect,
  useRef,
} from 'react';
import cn from 'classnames';
import { NavLinkProps, useLocation, useNavigate } from 'react-router-dom';
import KeyboardShortcut, { KeyWithTrigger } from '@components/KeyboardShortcut';
import { SVGProps } from '@components/Icons/SVGWrapper';
import styles from './NavigationItem.module.scss';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import NotificationDot from '@components/NotificationDot';

export interface NavigationItemDetails {
  IconComponent: (props: SVGProps) => ReactElement<any, any>;
  text: string;
  to: string;
  keyChar?: string;
  scaleOnHover?: boolean;
}

type NavItemProps = NavLinkProps &
  NavigationItemDetails & {
    isBeingDraggedOver?: boolean;
    className?: string;
    innerRef?: (element: HTMLElement) => void;
    // active?: boolean;
    scrollToOnActive?: boolean;
  };

interface NavButtonProps {
  IconComponent: (props: SVGProps) => ReactElement<any, any>;
  text: string;
  className?: string;
  onClick?: MouseEventHandler;
  id?: string;
}

export const NavButton = forwardRef(
  (
    {
      className,
      IconComponent,
      text,
      onClick,
      hasAlert,
      active,
      scrollToOnActive,
      ...props
    }: NavButtonProps & {
      onMouseEnter?: () => void;
      onMouseLeave?: () => void;
      hasAlert?: boolean;
      active?: boolean;
      scrollToOnActive?: boolean;
    },
    ref: RefObject<HTMLSpanElement>,
  ) => {
    const titleRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      if (active && scrollToOnActive && titleRef.current) {
        titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, [active]);

    return (
      <span ref={ref} className={cn(className, styles.navigationItem)} onClick={onClick} {...props}>
        <IconComponent className={styles.navigationIcon} />
        <span ref={titleRef} className={cn(styles.navigationItemText, 'navigation-item-label')}>
          {text}
        </span>
        <StateBasedFadeTransition
          state={String(!!hasAlert)}
          elementByStateMap={{
            true: <NotificationDot className={styles.notificationDot} />,
            false: null,
          }}
          duration="medium"
        />
      </span>
    );
  },
);

export default function NavigationItem({
  IconComponent,
  text,
  keyChar,
  className,
  scaleOnHover,
  isBeingDraggedOver,
  active,
  onMouseEnter,
  onMouseLeave,
  hasAlert,
  scrollToOnActive,
  ...props
}: NavItemProps & {
  active?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  hasAlert?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  let routeMatched = location.pathname == props.to;

  if (location.pathname == '/archive' && props.to == '/people') {
    routeMatched = true;
  }

  const button = (
    <NavButton
      id={props.id}
      className={cn(
        className,
        scaleOnHover && styles.scaleOnHover,
        scaleOnHover && styles[`hover${text}`],
        isBeingDraggedOver && styles.isBeingDraggedOver,
        (routeMatched || active) && styles.active,
      )}
      IconComponent={IconComponent}
      text={text}
      onClick={() => navigate(props.to)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      hasAlert={hasAlert}
      ref={props.innerRef}
      scrollToOnActive={scrollToOnActive}
      active={routeMatched || active}
    />
  );

  if (keyChar) {
    return (
      <KeyboardShortcut shortcut={<KeyWithTrigger keyChar={keyChar} />}>{button}</KeyboardShortcut>
    );
  }

  return button;
}
